// ⌘K: one search for structures, views, scenarios and actions. While it's open, the structures
// that match are highlighted in 3D and everything else is ghosted.

import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { Clapperboard, Eye, Images, Link, RotateCcw, Route, Search, Tag } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { search, nearestNames } from '../data/search.ts';
import { groupLabel, hemisphereLabel, isContext, systemSwatch } from '../data/catalogue.ts';
import { SCENARIOS, scenarioById } from '../scenarios/index.ts';
import { offeredPresets } from '../app/scene.ts';
import { Kbd } from '../ui/primitives.tsx';
import { shareUrl } from '../app/url.ts';
import { copyToClipboard } from './Toast.tsx';
import { startTour } from './Tour.tsx';
import { sceneTour } from './tours.ts';
import { findCards, loadGallery, STATE_LABEL, type Gallery } from '../data/gallery.ts';

export function Palette() {
  const open = useAtlas((s) => s.paletteOpen);
  const setPalette = useAtlas((s) => s.setPalette);
  const catalogue = useAtlas((s) => s.catalogue);
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const packs = useAtlas((s) => s.engine.packs);
  const journeys = useAtlas((s) => s.engine.journeys);
  const layers = useAtlas((s) => s.engine.layers);
  const kind = useAtlas((s) => s.engine.kind);
  const setQuery = useAtlas((s) => s.setQuery);
  const [q, setQ] = useState('');
  const [gallery, setGallery] = useState<Gallery | null>(null);

  const pool = useMemo(() => catalogue?.structures.filter((s) => !isContext(s)) ?? [], [catalogue]);
  const hits = useMemo(() => search(pool, q, 24), [pool, q]);
  const suggestions = useMemo(() => (q && !hits.length ? nearestNames(pool, q) : []), [pool, q, hits.length]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setQuery('', null);
    }
  }, [open, setQuery]);
  useEffect(() => {
    if (open) setQuery(q, q ? hits.map((h) => h.s.id) : null);
  }, [open, q, hits, setQuery]);
  // The gallery's cards are searchable too, once it has loaded (84 KB, read when the palette first opens).
  useEffect(() => {
    if (open && !gallery) void loadGallery().then(setGallery);
  }, [open, gallery]);
  // Best match first (a title match before a line match before one in the Methods), showcase order within a rank.
  const cards = useMemo(() => (q && gallery ? findCards(gallery, q) : []), [gallery, q]);
  // The scene's own tour (tours.ts sceneTour), once the gallery is in.
  const ownTour = useMemo(() => (gallery ? sceneTour(scenario.id, gallery) : null), [gallery, scenario.id]);

  const close = () => setPalette(false);
  const choose = (id: string) => {
    const s = useAtlas.getState();
    close();
    s.select(id);
    s.camera({ frame: { id } });
  };
  const run = (fn: () => void) => () => {
    close();
    fn();
  };
  const lq = q.toLowerCase();
  const views = offeredPresets(scenario, { packs, journeys, layers, kind }).filter((p) => !q || p.name.toLowerCase().includes(lq) || p.hint.toLowerCase().includes(lq));
  const scenes = SCENARIOS.filter((sc) => !q || sc.name.toLowerCase().includes(lq) || sc.tagline.toLowerCase().includes(lq));

  return (
    <Command.Dialog open={open} onOpenChange={setPalette} label="Search" className="palette" overlayClassName="palette-overlay" shouldFilter={false} loop data-label-exclude="">
      <div className="palette-input">
        <Search size={16} strokeWidth={1.5} />
        <Command.Input value={q} onValueChange={setQ} placeholder="Search structures, views and scenes" autoFocus />
        <Kbd>Esc</Kbd>
      </div>
      <Command.List className="palette-list">
        <Command.Empty className="palette-empty">
          {suggestions.length ? (
            <>
              <span>No match. Did you mean </span>
              {suggestions.map((s, i) => (
                <span key={s.id}>
                  <button type="button" className="link-btn" onClick={() => setQ(s.name)}>
                    {s.name}
                  </button>
                  {i < suggestions.length - 1 ? ', ' : '?'}
                </span>
              ))}
            </>
          ) : (
            'No results'
          )}
        </Command.Empty>
        {hits.length ? (
          <Command.Group heading="Structures">
            {hits.map(({ s, matched }) => (
              <Command.Item key={s.id} value={s.id} onSelect={() => choose(s.id)} onMouseEnter={() => useAtlas.getState().hover(s.id, 'ui')} onMouseLeave={() => useAtlas.getState().hover(null, 'ui')}>
                <span className="swatch" style={{ background: systemSwatch(s.system, s.color) }} aria-hidden="true" />
                <span className="item-main">
                  <span className="item-name">{s.name}</span>
                  {matched !== s.name ? <span className="item-sub">{matched}</span> : null}
                </span>
                <span className="item-meta">{[groupLabel(catalogue, s.system), hemisphereLabel(s.hemisphere)].filter(Boolean).join(' · ')}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {views.length ? (
          <Command.Group heading={`${scenario.name} views`}>
            {views.map((p) => (
              <Command.Item key={p.id} value={`view:${p.id}`} onSelect={run(() => useAtlas.getState().applyPreset(p))}>
                <Eye size={14} strokeWidth={1.5} />
                <span className="item-main">
                  <span className="item-name">{p.name}</span>
                </span>
                <span className="item-meta">{p.hint}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {scenes.length ? (
          <Command.Group heading="Scenes">
            {scenes.map((sc) => (
              <Command.Item key={sc.id} value={`scene:${sc.id}`} onSelect={run(() => useAtlas.getState().setScenario(sc.id))}>
                <Clapperboard size={14} strokeWidth={1.5} />
                <span className="item-main">
                  <span className="item-name">{sc.name}</span>
                </span>
                <span className="item-meta">
                  <Kbd>{sc.key}</Kbd>
                </span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {cards.length ? (
          <Command.Group heading="Gallery">
            {cards.map((it) => (
              <Command.Item key={it.id} value={`gallery:${it.id}`} onSelect={run(() => useAtlas.getState().setGallery(true, it.id))}>
                <Images size={14} strokeWidth={1.5} />
                <span className="item-main">
                  <span className="item-name">{it.title}</span>
                  <span className="item-sub">{it.line}</span>
                </span>
                <span className="item-meta">{STATE_LABEL[it.state]}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {!q ? (
          <Command.Group heading="Actions">
            <Command.Item value="action:tour" onSelect={run(() => startTour())}>
              <Route size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Start the tour</span>
                <span className="item-sub">The brain at every scale, from the whole organ to one cell, in thirteen steps</span>
              </span>
              <span className="item-meta">
                <Kbd>T</Kbd>
              </span>
            </Command.Item>
            {ownTour ? (
              <Command.Item value="action:tour-scene" onSelect={run(() => startTour(scenario.id))}>
                <Route size={14} strokeWidth={1.5} />
                <span className="item-main">
                  <span className="item-name">{`Tour ${scenario.name}’s cards`}</span>
                  <span className="item-sub">{ownTour.blurb}</span>
                </span>
                <span className="item-meta">
                  <Kbd>⇧</Kbd>
                  <Kbd>T</Kbd>
                </span>
              </Command.Item>
            ) : null}
            <Command.Item value="action:gallery" onSelect={run(() => useAtlas.getState().setGallery(true))}>
              <Images size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Browse the gallery</span>
                <span className="item-sub">Every shot and result, beside the scene it belongs to</span>
              </span>
              <span className="item-meta">
                <Kbd>G</Kbd>
              </span>
            </Command.Item>
            <Command.Item value="action:reset" onSelect={run(() => useAtlas.getState().resetView())}>
              <RotateCcw size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Reset the view</span>
              </span>
              <span className="item-meta">
                <Kbd>R</Kbd>
              </span>
            </Command.Item>
            <Command.Item value="action:labels" onSelect={run(() => useAtlas.getState().toggleLabels())}>
              <Tag size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Show or hide labels</span>
              </span>
              <span className="item-meta">
                <Kbd>L</Kbd>
              </span>
            </Command.Item>
            <Command.Item value="action:link" onSelect={run(() => void copyToClipboard(shareUrl()))}>
              <Link size={14} strokeWidth={1.5} />
              <span className="item-main">
                <span className="item-name">Copy a link to this view</span>
                <span className="item-sub">The scene, its controls, the time and the camera, as they are</span>
              </span>
            </Command.Item>
          </Command.Group>
        ) : null}
      </Command.List>
    </Command.Dialog>
  );
}
